import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, ArrowRight, CheckCircle, Loader2, AlertCircle, Linkedin, ArrowUpRight } from 'lucide-react';
import SEOMeta, { PAGE_META } from '../components/SEOMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/email/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed to send'); }
      setStatus('success');
    } catch (err) { setStatus('error'); setErrorMsg(err.message); }
  };

  const inputClass = "w-full bg-white/[0.05] border border-white/[0.10] px-5 py-4 text-base text-cream placeholder-cream/30 font-light tracking-wide focus:outline-none focus:border-gold/40 focus:bg-white/[0.08] transition-all disabled:opacity-50";

  if (status === 'success') {
    return (
      <div className="bg-navy min-h-screen" data-testid="contact-page">
        <SEOMeta url="/contact" {...PAGE_META.contact} />
        <div className="pt-40 pb-20 max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="border border-white/[0.07] p-14" data-testid="contact-success-message">
            <div className="w-16 h-16 bg-green-500/10 flex items-center justify-center mx-auto mb-6"><CheckCircle size={28} className="text-green-400" /></div>
            <h2 className="font-serif text-3xl text-cream font-light tracking-tight mb-3">Message Sent</h2>
            <p className="text-cream/60 text-base tracking-wide mb-8">We'll respond within 2 business days.</p>
            <button onClick={() => { setStatus('idle'); setFormData({ name: '', email: '', subject: '', message: '' }); }} className="text-gold text-[12px] uppercase tracking-[0.12em] hover:text-gold-light">
              Send another <ArrowRight size={12} className="inline ml-1" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-navy min-h-screen" data-testid="contact-page">
      <SEOMeta url="/contact" {...PAGE_META.contact} />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-gold/[0.02] to-transparent" /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-6">Contact</span>
          <h1 className="font-serif text-5xl sm:text-6xl text-cream font-light tracking-tighter leading-[0.92] mb-6">
            Get in <span className="text-gold">Touch</span>
          </h1>
          <p className="text-cream/75 text-base font-light leading-relaxed tracking-wide max-w-md">Questions about cultural leadership, speaking engagements, or consulting. We'd love to hear from you.</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      {/* Form + Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-7">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                {status === 'error' && (
                  <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 p-4 mb-6" data-testid="contact-error-message">
                    <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm">{errorMsg}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] text-cream/50 mb-2.5 uppercase tracking-[0.2em] font-medium">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required disabled={status === 'loading'} data-testid="contact-name-input" className={inputClass} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-cream/50 mb-2.5 uppercase tracking-[0.2em] font-medium">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={status === 'loading'} data-testid="contact-email-input" className={inputClass} placeholder="you@example.com" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-[11px] text-cream/50 mb-2.5 uppercase tracking-[0.2em] font-medium">Subject *</label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleChange} required disabled={status === 'loading'} data-testid="contact-subject-input" className={inputClass} placeholder="How can we help?" />
                </div>
                <div className="mb-8">
                  <label className="block text-[11px] text-cream/50 mb-2.5 uppercase tracking-[0.2em] font-medium">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required disabled={status === 'loading'} rows="6" data-testid="contact-message-input" className={`${inputClass} resize-none`} placeholder="Tell us more..." />
                </div>
                <button type="submit" disabled={status === 'loading'} data-testid="contact-submit-button" className="btn-editorial border border-gold text-gold px-8 py-4 text-sm uppercase tracking-[0.12em] font-sans font-medium inline-flex items-center gap-3 disabled:opacity-40">
                  {status === 'loading' ? <><Loader2 size={14} className="animate-spin" /><span>Sending...</span></> : <><span>Send Message</span> <Send size={14} /></>}
                </button>
              </form>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-white/[0.07] bg-white/[0.025] p-8 md:p-10 mb-6">
                <span className="block text-[11px] tracking-[0.2em] uppercase text-gold/80 mb-6 font-medium">Direct Contact</span>
                <div className="space-y-5">
                  {[
                    { Icon: Mail, label: 'Email', value: 'contact@thirdcultureleadership.co.uk', href: 'mailto:contact@thirdcultureleadership.co.uk' },
                    { Icon: Phone, label: 'Phone', value: '+44 7359 994352', href: 'tel:+447359994352' },
                    { Icon: MapPin, label: 'Location', value: 'United Kingdom', href: null },
                    { Icon: Linkedin, label: 'LinkedIn', value: 'linkedin.com/in/promisemonday', href: 'https://www.linkedin.com/in/promisemonday' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <item.Icon size={16} className="text-gold/50 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-cream/40 text-[10px] uppercase tracking-[0.15em] mb-0.5">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="text-cream/80 text-base font-light hover:text-gold transition-colors">{item.value}</a>
                        ) : (
                          <p className="text-cream/80 text-base font-light">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-white/[0.07] bg-white/[0.025] p-8">
                <span className="block text-[11px] tracking-[0.2em] uppercase text-gold/80 mb-3 font-medium">Response Time</span>
                <p className="text-cream/65 text-base font-light leading-[1.8] tracking-wide">We respond within 2 business days. For speaking engagements and urgent matters, indicate this in your message.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
